import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../../lib/supabaseClient';
import { usePlayerStore } from './playerStore';
import { PlayerEpisode, StoryReference } from './types';
import { getDisplayImageUrl, getPlayableEpisodeUrl } from '../utils/media';
import { slugify } from '../../utils/slug';

type PlayerController = {
  startSeriesPlayback: (story: StoryReference, options?: { startEpisodeId?: string }) => Promise<void>;
  togglePlay: () => Promise<void>;
  play: () => Promise<void>;
  pause: () => void;
  seek: (time: number) => void;
  next: () => Promise<void>;
  previous: () => Promise<void>;
  selectEpisode: (episodeId: string) => Promise<void>;
  markCompleted: (episodeId?: string) => Promise<void>;
  openQueue: (open: boolean) => void;
  openFullView: (open: boolean) => void;
};

const PlayerContext = createContext<PlayerController | null>(null);

const DEMO_USER_ID = import.meta.env.VITE_SUPABASE_DEMO_USER_ID as string | undefined;
const FALLBACK_COVER =
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=800&q=80';

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const lastProgressSyncRef = useRef<number>(0);
  const lastAnalyticsFlushRef = useRef<number>(0);
  const bufferedSecondsRef = useRef<number>(0);
  const lastPositionRef = useRef<number>(0);
  const userIdRef = useRef<string | null>(DEMO_USER_ID ?? null);
  const currentEpisodeIdRef = useRef<string | null>(null);
  const [userId, setUserId] = useState<string | null>(DEMO_USER_ID ?? null);

  const {
    queue,
    currentEpisodeId,
    isPlaying,
    volume,
    setQueue,
    setCurrentEpisodeId,
    setPlaying,
    setBuffering,
    setTime,
    setDuration,
    setQueueOpen,
    setFullViewOpen,
    setBufferedDelta,
    setError,
    reset,
  } = usePlayerStore();

  const currentEpisode = useMemo(
    () => queue.find((item) => item.id === currentEpisodeId) ?? null,
    [currentEpisodeId, queue],
  );

  useEffect(() => {
    currentEpisodeIdRef.current = currentEpisodeId;
  }, [currentEpisodeId]);

  const insertListeningEvent = useCallback(
    async (eventType: string, positionSeconds: number, deltaSeconds?: number | null) => {
      const episodeId = currentEpisodeIdRef.current;
      if (!userIdRef.current || !episodeId) return;
      const payload = {
        user_id: userIdRef.current,
        episode_id: episodeId,
        event_type: eventType,
        position_seconds: Math.floor(positionSeconds),
        delta_seconds: deltaSeconds ?? null,
      };
      const { error } = await supabase.from('listening_events').insert(payload);
      if (error) {
        console.error(`Error inserting listening event ${eventType}:`, error);
        const detail = [error.code, error.message, error.details].filter(Boolean).join(' • ');
        setError(`Playback analytics failed: ${detail || 'unknown error'}`);
      }
    },
    [currentEpisodeId, setError],
  );

  const setAudioSource = useCallback(
    async (episode: PlayerEpisode | null, autoPlay: boolean) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!episode?.audioUrl) {
        setError('The selected episode is missing an audio file.');
        return;
      }
      setBuffering(true);
      audio.src = episode.audioUrl;
      audio.load();
      try {
        if (autoPlay) {
          await audio.play();
          setPlaying(true);
          lastPositionRef.current = audio.currentTime;
          await insertListeningEvent('play_start', audio.currentTime, null);
        } else {
          setPlaying(false);
          lastPositionRef.current = audio.currentTime;
        }
      } catch (err) {
        console.error('Playback error:', err);
        setError('Unable to start playback. Please try again.');
        setPlaying(false);
      } finally {
        setBuffering(false);
      }
    },
    [insertListeningEvent, setBuffering, setError, setPlaying],
  );

  const loadProgressForEpisode = useCallback(
    async (episodeId: string): Promise<{ position: number; durationSeconds?: number | null }> => {
      if (!episodeId || !userIdRef.current) {
        return { position: 0, durationSeconds: null };
      }
      const { data, error } = await supabase
        .from('listening_progress')
        .select('position_seconds, duration_seconds')
        .eq('user_id', userIdRef.current)
        .eq('episode_id', episodeId)
        .maybeSingle();
      if (error) {
        console.error('Error loading progress:', error);
        return { position: 0, durationSeconds: null };
      }
      return {
        position: data?.position_seconds ?? 0,
        durationSeconds: data?.duration_seconds ?? null,
      };
    },
    [],
  );

  const upsertProgress = useCallback(
    async (positionSeconds: number, completed = false, durationSeconds?: number | null) => {
      const episodeId = currentEpisodeIdRef.current;
      if (!userIdRef.current || !episodeId) return;
      if (!completed) {
        const now = Date.now();
        if (now - lastProgressSyncRef.current < 10000) return;
        lastProgressSyncRef.current = now;
      }
      const payload = {
        user_id: userIdRef.current,
        episode_id: episodeId,
        position_seconds: Math.max(0, Math.floor(positionSeconds)),
        duration_seconds: durationSeconds != null ? Math.floor(durationSeconds) : null,
        last_listened_at: new Date().toISOString(),
        is_completed: completed,
        completed_at: completed ? new Date().toISOString() : null,
      };
      const { error } = await supabase.from('listening_progress').upsert(payload, {
        onConflict: 'user_id,episode_id',
      });
      if (error) {
        console.error('Error saving listening progress:', error);
      }
    },
    [],
  );

  const flushAnalyticsProgress = useCallback(
    async (positionSeconds: number, force = false) => {
      const buffered = bufferedSecondsRef.current;
      const now = Date.now();
      if (!force && now - lastAnalyticsFlushRef.current < 60000) {
        return;
      }
      if (buffered > 0 || force) {
        await insertListeningEvent('progress', positionSeconds, Math.max(0, Math.round(buffered)));
      }
      bufferedSecondsRef.current = 0;
      setBufferedDelta(0);
      lastAnalyticsFlushRef.current = now;
    },
    [insertListeningEvent, setBufferedDelta],
  );

  const completeEpisode = useCallback(
    async (positionSeconds?: number) => {
      const audio = audioRef.current;
      const durationSeconds = audio?.duration ?? currentEpisode?.durationSeconds ?? null;
      const rawPosition =
        typeof positionSeconds === 'number'
          ? positionSeconds
          : Math.max(audio?.currentTime ?? 0, durationSeconds ? durationSeconds - 1 : 0);
      const finalPosition = durationSeconds
        ? Math.max(Math.floor(durationSeconds), Math.floor(rawPosition))
        : Math.floor(rawPosition);

      await flushAnalyticsProgress(finalPosition, true);
      await insertListeningEvent('complete', finalPosition, null);
      await upsertProgress(finalPosition, true, durationSeconds ?? null);
    },
    [currentEpisode?.durationSeconds, flushAnalyticsProgress, insertListeningEvent, upsertProgress],
  );

  const playEpisodeAtIndex = useCallback(
    async (index: number, autoPlay = true) => {
      if (index < 0 || index >= queue.length) return;
      const audio = audioRef.current;
      if (audio && currentEpisodeId) {
        void flushAnalyticsProgress(audio.currentTime, true);
        void upsertProgress(audio.currentTime, false, audio.duration || currentEpisode?.durationSeconds);
      }
      const target = queue[index];
      setCurrentEpisodeId(target.id);
      setTime(0);
      const { position } = await loadProgressForEpisode(target.id);
      pendingSeekRef.current = position;
      await setAudioSource(target, autoPlay);
    },
    [
      currentEpisode?.durationSeconds,
      currentEpisodeId,
      flushAnalyticsProgress,
      loadProgressForEpisode,
      queue,
      setAudioSource,
      setCurrentEpisodeId,
      setTime,
      upsertProgress,
    ],
  );

  const playNext = useCallback(async () => {
    const currentIndex = queue.findIndex((ep) => ep.id === currentEpisodeId);
    if (currentIndex === -1 || currentIndex >= queue.length - 1) return;
    await playEpisodeAtIndex(currentIndex + 1, true);
  }, [currentEpisodeId, playEpisodeAtIndex, queue]);

  const playPrevious = useCallback(async () => {
    const currentIndex = queue.findIndex((ep) => ep.id === currentEpisodeId);
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      setTime(0);
      return;
    }
    if (currentIndex > 0) {
      await playEpisodeAtIndex(currentIndex - 1, true);
    }
  }, [currentEpisodeId, playEpisodeAtIndex, queue, setTime]);

  const hydrateQueue = useCallback(
    async (story: StoryReference, options?: { startEpisodeId?: string; autoPlay?: boolean }) => {
      const { startEpisodeId, autoPlay = true } = options ?? {};
      const { data, error } = await supabase
        .from('episodes')
        .select(
          `
          id,
          title,
          slug,
          duration_seconds,
          episode_number,
          audio_file_url,
          cover_image_url,
          description,
          series:series_id (
            id,
            title,
            slug,
            cover_image_url,
            creator:profiles!series_creator_id_fkey (
              id,
              display_name
            )
          )
        `,
        )
        .eq('series_id', story.id)
        .eq('status', 'published')
        .order('episode_number', { ascending: true });

      if (error) {
        console.error('Error loading episodes for player:', error);
        setError('Something went wrong while loading this series. Please try again.');
        return;
      }

      const mapped: PlayerEpisode[] = [];
      for (const row of data ?? []) {
        const creatorName =
          row.series?.creator?.display_name ?? story.creatorName ?? story.author ?? 'Unknown Creator';
        const creatorSlug = story.creatorSlug ?? (creatorName ? slugify(creatorName) : null);
        const [audioUrl, coverImage] = await Promise.all([
          getPlayableEpisodeUrl(row.audio_file_url),
          getDisplayImageUrl(row.cover_image_url ?? story.coverImage ?? FALLBACK_COVER),
        ]);
        if (!audioUrl) continue;
        mapped.push({
          id: row.id,
          title: row.title,
          slug: row.slug,
          episodeNumber: row.episode_number,
          durationSeconds: row.duration_seconds,
          audioUrl,
          coverImage: coverImage ?? story.coverImage,
          description: row.description,
          seriesId: row.series?.id ?? story.id,
          seriesTitle: row.series?.title ?? story.title,
          seriesSlug: row.series?.slug ?? story.slug,
          creatorName,
          creatorSlug,
        });
      }

      if (!mapped.length) {
        setError('No playable episodes were found for this series. Please check your audio uploads.');
        return;
      }

      const requestedIndex = startEpisodeId
        ? mapped.findIndex((ep) => ep.id === startEpisodeId)
        : 0;
      const startIndex = requestedIndex >= 0 ? requestedIndex : 0;
      setQueue(mapped, mapped[startIndex]?.id ?? null);
      setQueueOpen(false);
      setFullViewOpen(false);
      const { position } = await loadProgressForEpisode(mapped[startIndex].id);
      pendingSeekRef.current = position;
      await setAudioSource(mapped[startIndex], autoPlay);
    },
    [
      loadProgressForEpisode,
      setAudioSource,
      setError,
      setFullViewOpen,
      setQueue,
      setQueueOpen,
    ],
  );

  const loadLatestInProgress = useCallback(async () => {
    if (!userIdRef.current) return;
    if (usePlayerStore.getState().queue.length > 0) return;
    const { data, error } = await supabase
      .from('listening_progress')
      .select(
        `
        episode_id,
        position_seconds,
        duration_seconds,
        last_listened_at,
        episodes:episode_id (
          id,
          title,
          slug,
          duration_seconds,
          episode_number,
          audio_file_url,
          cover_image_url,
          description,
          series:series_id (
            id,
            title,
            slug,
            cover_image_url,
            creator:profiles!series_creator_id_fkey (
              id,
              display_name
            )
          )
        )
      `,
      )
      .eq('user_id', userIdRef.current)
      .eq('is_completed', false)
      .gt('position_seconds', 0)
      .order('last_listened_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error loading latest progress for player:', error);
      return;
    }

    if (!data?.episodes) return;
    const episode = data.episodes;
    const series = episode.series;
    const creatorName = series?.creator?.display_name ?? 'Unknown Creator';
    const story: StoryReference = {
      id: series?.id ?? '',
      title: series?.title ?? 'Series',
      slug: series?.slug ?? episode.slug ?? null,
      author: creatorName,
      creatorSlug: creatorName ? slugify(creatorName) : null,
      coverImage: (await getDisplayImageUrl(series?.cover_image_url)) ?? FALLBACK_COVER,
      creatorName,
    };

    if (!story.id) return;
    await hydrateQueue(story, { startEpisodeId: episode.id, autoPlay: false });
  }, [hydrateQueue]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      await audio.play();
      setPlaying(true);
      lastPositionRef.current = audio.currentTime;
      await insertListeningEvent('play_start', audio.currentTime, null);
    } else {
      await flushAnalyticsProgress(audio.currentTime, true);
      await insertListeningEvent('pause', audio.currentTime, null);
      audio.pause();
      setPlaying(false);
    }
  }, [flushAnalyticsProgress, insertListeningEvent, setPlaying]);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      await audio.play();
      setPlaying(true);
      lastPositionRef.current = audio.currentTime;
      await insertListeningEvent('play_start', audio.currentTime, null);
    } catch (err) {
      console.error('Playback error:', err);
      setError('Unable to start playback. Please try again.');
      setPlaying(false);
    }
  }, [insertListeningEvent, setError, setPlaying]);

  const pause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    await flushAnalyticsProgress(audio.currentTime, true);
    await insertListeningEvent('pause', audio.currentTime, null);
    setPlaying(false);
  }, [flushAnalyticsProgress, insertListeningEvent, setPlaying]);

  const seek = useCallback(
    async (time: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      const target = Math.max(0, Math.min(time, audio.duration || time));
      await flushAnalyticsProgress(audio.currentTime, true);
      audio.currentTime = target;
      setTime(target);
      lastPositionRef.current = target;
      bufferedSecondsRef.current = 0;
      setBufferedDelta(0);
      await insertListeningEvent('seek', target, null);
    },
    [flushAnalyticsProgress, insertListeningEvent, setBufferedDelta, setTime],
  );

  const onTimeUpdate = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    const position = audio.currentTime;
    const previous = lastPositionRef.current;
    setTime(position);
    if (audio.duration && !Number.isNaN(audio.duration)) {
      setDuration(audio.duration);
    }
    if (isPlaying) {
      const delta = Math.max(0, position - previous);
      bufferedSecondsRef.current += delta;
      setBufferedDelta(bufferedSecondsRef.current);
    }
    lastPositionRef.current = position;
    await upsertProgress(position, false, audio.duration || currentEpisode?.durationSeconds);
    await flushAnalyticsProgress(position);
  }, [currentEpisode?.durationSeconds, flushAnalyticsProgress, isPlaying, setBufferedDelta, setDuration, setTime, upsertProgress]);

  const onLoadedMetadata = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(audio.duration);
    if (pendingSeekRef.current != null) {
      audio.currentTime = pendingSeekRef.current;
      setTime(pendingSeekRef.current);
      pendingSeekRef.current = null;
    }
    const durationSeconds = Number.isFinite(audio.duration) ? audio.duration : null;
    if (durationSeconds) {
      await upsertProgress(audio.currentTime || 0, false, durationSeconds);
    }
  }, [setDuration, setTime, upsertProgress]);

  const onEnded = useCallback(async () => {
    const audio = audioRef.current;
    const position = audio?.currentTime ?? 0;
    await flushAnalyticsProgress(position, true);
    const endPosition = Math.floor(audio?.duration ?? position);
    await insertListeningEvent('play_end', endPosition, null);
    await completeEpisode(endPosition);
    await playNext();
    const { queue: latestQueue, currentEpisodeId: latestId } = usePlayerStore.getState();
    const currentIndex = latestQueue.findIndex((ep) => ep.id === latestId);
    if (currentIndex === -1 || currentIndex >= latestQueue.length - 1) {
      setPlaying(false);
      setCurrentEpisodeId(null);
      setQueue([], null);
      setQueueOpen(false);
      setFullViewOpen(false);
      setTime(0);
      setDuration(0);
    }
  }, [
    completeEpisode,
    flushAnalyticsProgress,
    insertListeningEvent,
    playNext,
    setCurrentEpisodeId,
    setDuration,
    setFullViewOpen,
    setPlaying,
    setQueue,
    setQueueOpen,
    setTime,
  ]);

  const startSeriesPlayback = useCallback(
    async (story: StoryReference, options?: { startEpisodeId?: string }) => {
      await hydrateQueue(story, { startEpisodeId: options?.startEpisodeId, autoPlay: true });
    },
    [hydrateQueue],
  );

  const markCompleted = useCallback(
    async (episodeId?: string) => {
      const targetId = episodeId ?? currentEpisodeId;
      if (!targetId) return;
      await completeEpisode(audioRef.current?.currentTime ?? 0);
      const hasNext = queue.findIndex((ep) => ep.id === targetId) < queue.length - 1;
      if (hasNext) {
        await playNext();
      } else {
        setPlaying(false);
        setCurrentEpisodeId(null);
        setQueue([], null);
        setQueueOpen(false);
        setFullViewOpen(false);
        setTime(0);
        setDuration(0);
      }
    },
    [
      completeEpisode,
      currentEpisodeId,
      playNext,
      queue,
      setCurrentEpisodeId,
      setDuration,
      setFullViewOpen,
      setPlaying,
      setQueue,
      setQueueOpen,
      setTime,
    ],
  );

  const selectEpisode = useCallback(
    async (episodeId: string) => {
      const index = queue.findIndex((ep) => ep.id === episodeId);
      if (index === -1) return;
      await playEpisodeAtIndex(index, true);
    },
    [playEpisodeAtIndex, queue],
  );

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const nextUserId = data.session?.user?.id ?? DEMO_USER_ID ?? null;
      userIdRef.current = nextUserId;
      setUserId(nextUserId);
      if (!nextUserId) {
        reset();
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      }
    });
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      const nextUserId = newSession?.user?.id ?? DEMO_USER_ID ?? null;
      const prevUserId = userIdRef.current;
      userIdRef.current = nextUserId;
      setUserId(nextUserId);
      if (!nextUserId || prevUserId !== nextUserId) {
        reset();
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      }
    });
    return () => {
      mounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, [reset]);

  useEffect(() => {
    if (!userId) return;
    loadLatestInProgress();
  }, [loadLatestInProgress, userId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTime = () => {
      onTimeUpdate();
    };
    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleWaiting = () => setBuffering(true);
    const handlePlaying = () => setBuffering(false);
    audio.addEventListener('timeupdate', handleTime);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);
    return () => {
      audio.removeEventListener('timeupdate', handleTime);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [onEnded, onLoadedMetadata, onTimeUpdate, setBuffering, setPlaying]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const audio = audioRef.current;
      if (!audio) return;
      void flushAnalyticsProgress(audio.currentTime, true);
      void upsertProgress(audio.currentTime, false, audio.duration || currentEpisode?.durationSeconds);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentEpisode?.durationSeconds, flushAnalyticsProgress, upsertProgress]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        const audio = audioRef.current;
        if (!audio) return;
        void flushAnalyticsProgress(audio.currentTime, true);
        void upsertProgress(audio.currentTime, false, audio.duration || currentEpisode?.durationSeconds);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [currentEpisode?.durationSeconds, flushAnalyticsProgress, upsertProgress]);

  useEffect(() => {
    if (!currentEpisode || typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentEpisode.title,
        artist: currentEpisode.creatorName ?? currentEpisode.seriesTitle,
        album: currentEpisode.seriesTitle,
        artwork: currentEpisode.coverImage
          ? [{ src: currentEpisode.coverImage, sizes: '512x512', type: 'image/png' }]
          : [],
      });
      navigator.mediaSession.setActionHandler('play', () => play());
      navigator.mediaSession.setActionHandler('pause', () => pause());
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (details.seekTime != null) {
          seek(details.seekTime);
        }
      });
      navigator.mediaSession.setActionHandler('previoustrack', () => playPrevious());
      navigator.mediaSession.setActionHandler('nexttrack', () => playNext());
    } catch (err) {
      console.warn('Media Session not available', err);
    }
  }, [currentEpisode, play, playNext, playPrevious, pause, seek]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!isPlaying && !audio.paused) {
      audio.pause();
    }
    if (isPlaying && audio.paused) {
      audio.play().catch((err) => {
        console.error('Playback error:', err);
        setError('Unable to start playback. Please try again.');
        setPlaying(false);
      });
    }
  }, [isPlaying, setError, setPlaying]);

  const contextValue = useMemo<PlayerController>(
    () => ({
      startSeriesPlayback,
      togglePlay,
      play,
      pause,
      seek,
      next: playNext,
      previous: playPrevious,
      selectEpisode,
      markCompleted,
      openQueue: setQueueOpen,
      openFullView: setFullViewOpen,
    }),
    [
      markCompleted,
      pause,
      play,
      playNext,
      playPrevious,
      seek,
      selectEpisode,
      setFullViewOpen,
      setQueueOpen,
      startSeriesPlayback,
      togglePlay,
    ],
  );

  return (
    <PlayerContext.Provider value={contextValue}>
      {children}
      <audio ref={audioRef} preload="metadata" className="hidden" />
    </PlayerContext.Provider>
  );
}

export const usePlayerController = (): PlayerController => {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    throw new Error('usePlayerController must be used within PlayerProvider');
  }
  return ctx;
};
